import React, { useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, IconButton,
  Checkbox, Container, FormControl, FormControlLabel,
  FormGroup, FormHelperText, Radio, RadioGroup,
  Stack, TextField, Typography,
} from '@mui/material';
import {
  Icons, useApiClient,
} from '@marageti/z4-lib';
import { useParams, useNavigate } from 'react-router-dom';
import {
  TripMatch, TripMatchStatus, TripMatchStatusUpdate,
  TripRequestStatus, TripRequestStatusUpdate,
} from '@marageti/z4-sdk/lib/travel';
import { LoadingButton } from '@mui/lab';

const buttonStyles = {
  mb: '20px',
  width: {
    mobile: '100%',
    desktop: '30%',
  },
  mx: {
    desktop: '10px',
    mobile: '0px',
  },
};

type CancelSelectionType = {
  label: string;
  value: string;
};

const cancelReasons: CancelSelectionType[] = [
  { value: 'notInterested', label: 'I am no longer interested in taking this trip' },
  { value: 'otherCompany', label: 'I am planning myself or with another company' },
  { value: 'other', label: 'Other' },
];

const partnerIssues: CancelSelectionType[] = [
  { value: 'comm', label: 'Communication issues' },
  { value: 'pricing', label: 'Pricing' },
  { value: 'itinerary', label: 'Itinerary' },
  { value: 'other', label: 'Other' },
];

const buildReason = (
  isCancelAll: boolean,
  cancelReason: string,
  partnerToCancel?: string,
  issues?: { [key: string]: boolean },
  feedbackText?: string,
): string => {
  const otherFeedback = !!feedbackText && feedbackText.trim().length > 0 ? `Additional Feedback:\n\n${feedbackText}` : '';
  let partnerIssuesText = '';
  let text = 'This trip has been canceled because:';

  if (isCancelAll) {
    const cancelText = cancelReasons.find((reason) => reason.value === cancelReason)?.label;
    text = `${text}\n\n${cancelText}\n\n${otherFeedback}`;
  } else {
    Object.entries(issues ?? {}).forEach(([key, value]) => {
      partnerIssuesText += value ? `${partnerIssues.find((reason) => reason.value === key)?.label}\n\n` : '';
    });
    text = `This trip matched with ${partnerToCancel} has been canceled because of:`;
    text = `${text}\n\n${partnerIssuesText}\n\n${otherFeedback}`;
  }
  return text;
};

const CancelTripHeader = ({ onBackButton }: { onBackButton: () => void }) => (
  <Stack
    direction="row"
    alignItems="center"
  >
    <Box
      flexGrow={1}
      flexBasis={0}
    >
      <IconButton
        aria-label="back to trip"
        onClick={() => onBackButton()}
        edge="start"
        size="small"
      >
        <Icons.ArrowLeft color="primary" />
      </IconButton>
    </Box>

    <Box
      flexGrow={1}
      flexBasis={0}
    >
      <Typography
        variant="h4"
        textAlign="center"
      >
        Cancel Request
      </Typography>
    </Box>

    <Box
      flexGrow={1}
      flexBasis={0}
    />
  </Stack>
);

type Props = {
  tripMatches: any[],
  onCancel: () => void,
  country: string
};

const TripCancelView = ({ tripMatches, onCancel, country }: Props) => {
  const navigate = useNavigate();
  const { tripRequestId } = useParams();
  const [isCancelAll, setIsCancelAll] = useState(true);
  const [cancelReason, setCancelReason] = useState(cancelReasons[0].value);
  const [selectedIssues, setSelectedIssues] = useState<{ [key: string]: boolean }>({});
  const [partnerToCancel, setPartnerToCancel] = useState(tripMatches && tripMatches.length > 0 ? tripMatches[0].partnerId : undefined);
  const [feedBackText, setFeedbackText] = useState('');
  const [isErrorCancel, setIsErrorCancel] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const apiClient = useApiClient();
  const hasMoreThanOneMatch = tripMatches && tripMatches.length > 1;
  const atLeastIssueSelected = Object.values(selectedIssues).some((value) => value === true);
  const canSubmit = isCancelAll || atLeastIssueSelected;

  const handleIsCancelAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsCancelAll(event.target.value === 'yes');
  };

  const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCancelReason(event.target.value);
  };

  const handleIssuesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedIssues({
      ...selectedIssues,
      [event.target.name]: event.target.checked,
    });
  };

  const handlePartnerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPartnerToCancel(event.target.value);
  };

  const handleFeedbackChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFeedbackText(event.target.value);
  };

  const handleSend = async () => {
    const reason = buildReason(isCancelAll, cancelReason, tripMatches.find((m) => m.partnerId === partnerToCancel)?.partnerName, selectedIssues, feedBackText);
    const cancelTripCall = () => apiClient.tripRequestsClient.putTripRequestStatus(tripRequestId ?? '', {
      id: tripRequestId,
      status: TripRequestStatus.CanceledStatus,
      statusInfo: {
        reason,
      },
    } as TripRequestStatusUpdate);
    setIsErrorCancel(false);
    setIsSubmitting(true);
    try {
      if (tripMatches.length > 0 && !isCancelAll) {
        let tripMatch = tripMatches[0] as TripMatch; // matched with just one partner, the user does not get to see/pick this on the page
        if (hasMoreThanOneMatch) tripMatch = tripMatches.find((match) => match.partnerId === partnerToCancel) as TripMatch;
        await apiClient.tripMatchesClient.putTripMatchStatus(tripMatch.id, {
          id: tripMatch.id,
          status: TripMatchStatus.MatchTravelerCanceled,
          statusInfo: {
            reason,
          },
        } as TripMatchStatusUpdate);
      }

      if (tripMatches.length < 2 || isCancelAll) {
        // cancel trip with no match or had only one match that had been cancelled above prior to this call
        await cancelTripCall();
        navigate('cancel-success?cancel-all=true');
      } else {
        navigate('cancel-success');
      }
    } catch (error) {
      setIsErrorCancel(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isErrorCancel) {
    return (
      <Box
        pl={4}
        pr={5}
        mt={4}
      >
        <Alert severity="error">
          <AlertTitle>Server Error</AlertTitle>
          There was a problem canceling your trip.
          Please try again by refreshing this page in a few minutes.
        </Alert>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box
        sx={{
          py: { desktop: 6.5, mobile: 3.5 },
          width: { desktop: '650px' },
          margin: { desktop: '0 auto 56px' },
          height: '100%',
        }}
      >
        {/* Back button / header */}
        <CancelTripHeader onBackButton={onCancel} />

        {/* cancel details */}
        <Box alignItems="left" mt="48px">
          <Stack>
            <Typography variant="h3">
              You are canceling your request for a trip to
              {' '}
              {country}
              .
            </Typography>
            <Typography variant="body" mt="11px">
              We’re sorry to see you go. If you have a few minutes, we’d like to know how we can improve.
            </Typography>

            {hasMoreThanOneMatch && (
              <>
                <Typography variant="h4" pt="24px">
                  Do you want to cancel the entire trip, including all specialists?
                </Typography>
                <FormControl>
                  <RadioGroup name="isCancelAll" onChange={handleIsCancelAllChange} value={isCancelAll ? 'yes' : 'no'}>
                    <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio />} label="No, just a specific specialist" />
                  </RadioGroup>
                </FormControl>
              </>
            )}

            {(!hasMoreThanOneMatch || isCancelAll) && (
              <>
                <Typography variant="h4" pt="30px">
                  Can you tell us why you are canceling?
                </Typography>
                <FormControl>
                  <RadioGroup name="cancelReasons" value={cancelReason} onChange={handleReasonChange}>
                    {
                      cancelReasons.map((reason) => <FormControlLabel key={reason.value} value={reason.value} control={<Radio />} label={reason.label} />)
                    }
                  </RadioGroup>
                </FormControl>
              </>
            )}
          </Stack>
        </Box>

        {/* partners to cancel */}
        {hasMoreThanOneMatch && !isCancelAll && (
          <Box>
            <Typography variant="h4" pt="30px">
              Which partner would you like to cancel?
            </Typography>
            <FormControl>
              <RadioGroup name="cancelReasons" value={partnerToCancel} onChange={handlePartnerChange}>
                {
                tripMatches.map((match) => <FormControlLabel key={match.partnerId} value={match.partnerId} control={<Radio />} label={match.partnerName} />)
              }
              </RadioGroup>
            </FormControl>
            <Typography variant="h4" pt="30px">
              Why don’t you want to work with this partner anymore? Zicasso can help you with any issues.*
            </Typography>
            <FormControl error={!atLeastIssueSelected}>
              <FormGroup>
                {partnerIssues.map((item) => (
                  <FormControlLabel
                    key={item.value}
                    control={(
                      <Checkbox
                        checked={selectedIssues[item.value] || false}
                        onChange={handleIssuesChange}
                        name={item.value}
                        color="primary"
                      />
                )}
                    label={item.label}
                  />
                ))}
              </FormGroup>
              {!atLeastIssueSelected && <FormHelperText id="my-input-error">Please select at least one issue.</FormHelperText>}
            </FormControl>
          </Box>
        )}

        {/* feedback text */}
        <TextField
          sx={{ height: '113px' }}
          InputProps={{
            style: { padding: '15px' },
          }}
          placeholder="We appreciate any additional feedback you want to share."
          fullWidth
          multiline
          rows={4}
          label="Additional Feedback"
          value={feedBackText}
          onChange={handleFeedbackChange}
        />

        {/* buttons */}
        <Box sx={{
          pt: '80px',
          display: {
            desktop: 'flex',
          },
          justifyContent: {
            desktop: 'center',
          },
        }}
        >
          <Button variant="outlined" sx={buttonStyles} onClick={onCancel}>Cancel</Button>
          <LoadingButton
            variant="contained"
            sx={buttonStyles}
            onClick={handleSend}
            loading={isSubmitting}
            disabled={!canSubmit}
          >
            Submit
          </LoadingButton>
        </Box>

      </Box>
    </Container>
  );
};
export default TripCancelView;
